import { Add, Edit, Search, TrashCan } from "@carbon/icons-react"
import { Image, Pressable, ScrollView, StyleSheet } from "react-native"
import { View } from "react-native-web"
import React, { Dispatch, useEffect, useState } from "react"
import { Datastore, useDatastore, useModuleMap, useModuleObject, useServerCallResult } from "util/datastore"
import { HorizBox, Pad, PadBox, Separator, ShadowBox } from "component/basics"
import { Banner } from "component/banner"
import { colorAmbiguousWhite, colorBorderPrimary, colorGreyHover, colorSurfacePrimary, colorSurfaceSecondary, colorTextSecondary, colorTextSecondaryOnLightColorBg, colorTextWarning } from "component/color"
import { Heading, TextField, UtilityText } from "component/text"
import { ActionButton, CTAButton, TextButton } from "component/button"
import { useHasCapability } from "component/admin"
import { EditorialChannel, moduleMapChannel, moduleMapChannelToInstance } from "open-modules/zdf/component/channels/channeltypes"
import { ToggleWithLabelWidget } from "open-modules/zdf/component/form"
import { Modal } from "component/modal"
import { Checkbox, FormField } from "component/form"
import { DocumentLevelTooltip, InfoTag } from "open-modules/zdf/component/tooltip"
import { isChannelNameValid, isValidEmail } from "open-modules/zdf/util/channel-util"
import { ActionButtonWithDisabledState } from "open-modules/zdf/component/button"
import { undefS } from "util/stdtypes"

export function ChannelManagement() {
    const canViewChannels = useHasCapability("channel/view");
    if (!canViewChannels) return;
    return (
        <View>
            <HorizBox spread>
                <Heading type="small" label="Manage Channels" weight="strong" />
                <ChannelCreationButton />
            </HorizBox>
            <Pad size={24} />
            <ChannelList />
        </View>
    );
}

function ChannelCreationButton() {
    const canCreateChannel = useHasCapability("channel/create-delete");
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    if (!canCreateChannel) return;
    return (
        <View>
            <CTAButton label="Add channel" onPress={() => setShowCreateDialog(true)} />
            {showCreateDialog && <ManageChannelDialog onClose={() => setShowCreateDialog(false)} isNewChannel={true} />}
        </View>
    );
}

function ChannelList() {
    const channelsObject = useModuleMap(moduleMapChannel);
    const channels = Object.values(channelsObject || {});
    const [channelToModifyKey, setChannelToModifyKey] = useState("");
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);

    function onEdit(channelKey) {
        setChannelToModifyKey(channelKey);
        setShowEditDialog(true);
    }

    function onDelete(channelKey) {
        setChannelToModifyKey(channelKey);
        setShowDeleteDialog(true);
    }

    return (
        <View>
            <ScrollView testID="channel-scroll-view">
                {channels.map((channel) => (
                    <ChannelTile
                        onEdit={onEdit}
                        onDelete={onDelete}
                        key={channel.key}
                        channel={channel}
                    />
                ))}
            </ScrollView>
            {showEditDialog && (
                <ManageChannelDialog
                    onClose={() => setShowEditDialog(false)}
                    channelKey={channelToModifyKey}
                    isNewChannel={false}
                />
            )}
            {showDeleteDialog && (
                <DeleteChannelDialog onClose={() => setShowDeleteDialog(false)} channelKey={channelToModifyKey} />
            )}
        </View>
    );
}

const ChannelTileStyle = StyleSheet.create({
    outerContainer: {
        flexDirection: "row",
        alignItems: "center",
        height: 50,
        backgroundColor: colorAmbiguousWhite,
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderBottomColor: colorBorderPrimary,
        borderBottomWidth: 1,
    },
    innerContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        width: "100%",
    },
    channelInfo: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        flexGrow: 0,
        flexShrink: 1,
        flexBasis: "auto",
        overflow: "hidden",
        gap: 8,
    },
    channelInfoText: {
        display: "flex",
        flexDirection: "row",
        alignItems: "baseline",
        gap: 8,
    },
    options: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 12,
        flexGrow: 0,
    },
});

type VoidCallBack<T> = (params:T) => void
function ChannelTile({ channel, onEdit, onDelete }: { channel: EditorialChannel, onEdit: VoidCallBack<string>, onDelete: VoidCallBack<string> }) {
    const s = ChannelTileStyle;
    const datastore = useDatastore();
    const canCreateDeleteChannel = useHasCapability("channel/create-delete");
    const canEditChannelInfo = useHasCapability("channel/edit-info");
    const canEditChannelMembers = useHasCapability("channel/edit-members");
    const [isChannelDeletable, setIsChannelDeletable] = useState<boolean | undefined>(undefined);
    const deleteTooltipLabel = "You cannot delete the default channel or channels that are in use";

    useEffect(() => {
        async function getIsDeletable() {
            const isDeletable = await checkIfChannelMayBeDeletedAsync({ datastore, channelKey: channel.key, isDefaultChannel: channel.isDefault ?? false });
            setIsChannelDeletable(isDeletable);
        }

        if (isChannelDeletable === undefined) {
            getIsDeletable();
        }
    }, []);

    return (
        <View style={s.outerContainer} key={channel.key}>
            <View style={s.innerContainer}>
                <View style={s.channelInfo}>
                    <ChannelImage imageUrl={channel.photoUrl} width={32} height={32} />
                    <View style={s.channelInfoText}>
                        <UtilityText type="large" weight="medium" text={channel.name} />
                        {channel.isDefault && <UtilityText label="Default channel" color={colorTextSecondary} />}
                    </View>
                </View>
                <View style={s.options}>
                    {canCreateDeleteChannel && (
                        <DocumentLevelTooltip label={deleteTooltipLabel} width={225} disabled={isChannelDeletable} testID={"delete-tooltip-" + channel.key}>
                            <ActionButtonWithDisabledState icon={TrashCan} onPress={() => onDelete(channel.key)} label="Delete" disabled={!isChannelDeletable} testID={"delete-" + channel.key} />
                        </DocumentLevelTooltip>
                    )}
                    {(canEditChannelInfo || canEditChannelMembers) && (
                        <ActionButton icon={Edit} onPress={() => onEdit(channel.key)} label="Edit" testID={"edit-" + channel.key} />
                    )}
                </View>
            </View>
        </View>
    );
}

const DeleteChannelDialogStyle = StyleSheet.create({
    container: {
        gap: 4,
        height: 200,
        justifyContent: "space-evenly",
        flexDirection: "column",
        paddingHorizontal: 20,
    },
    textContent: { justifyContent: "flex-start", gap: 4 },
    buttonRow: { flexDirection: "row", width: "100%", gap: 8 },
});

/** Checks if any question or topic instances are assigned to the channel */
async function checkIfChannelIsInUseAsync({ datastore, channelKey }): Promise<boolean> {
    const channelQuestions = await datastore.getModuleObjectAsync(moduleMapChannelToInstance, channelKey + "/question");
    const channelTopics = await datastore.getModuleObjectAsync(moduleMapChannelToInstance, channelKey + "/topic");
    return channelQuestions !== null || channelTopics !== null;
}

async function checkIfChannelMayBeDeletedAsync({ datastore, channelKey, isDefaultChannel }: { datastore: Datastore; channelKey: string; isDefaultChannel: boolean }): Promise<boolean> {
    const isChannelInUse = await checkIfChannelIsInUseAsync({ datastore, channelKey });
    return !isDefaultChannel && !isChannelInUse;
}

function DeleteChannelDialog({ channelKey, onClose }) {
    const s = DeleteChannelDialogStyle;
    const datastore = useDatastore();
    const channel = useModuleObject(moduleMapChannel, channelKey);
    const [isChannelDeletable, setIsChannelDeletable] = useState<boolean | undefined>(undefined);
    
    useEffect(() => {
        async function getIsDeletable() {
            const isDeletable = await checkIfChannelMayBeDeletedAsync({ datastore, channelKey, isDefaultChannel: channel?.isDefault ?? false });
            setIsChannelDeletable(isDeletable);
        }

        if (isChannelDeletable === undefined) {
            getIsDeletable();
        }
    }, []);

    return (
        <Modal onClose={onClose} showTopBar={false}>
            <View style={s.container}>
                <View style={s.textContent}>
                    <Heading label="Delete Channel" weight="medium" type="large" />
                    <UtilityText
                        label="Should this channel really be deleted?"
                        color={colorTextSecondary}
                        type="large"
                    />
                </View>
                <View style={s.buttonRow}>
                    <CTAButton type="secondary" label="Cancel" onPress={() => onClose()} wide />
                    <CTAButton
                        type="delete"
                        disabled={!isChannelDeletable}
                        label="Delete"
                        icon={<TrashCan color={colorTextWarning} />}
                        onPress={async () => {
                            await datastore.callServerAsync("channel", "deleteChannel", { channelKey });
                            onClose();
                        }}
                        wide
                    />
                </View>
            </View>
        </Modal>
    );
}

export function ChannelImage({ imageUrl, width = 45, height = 45 }) {
    if (imageUrl) {
        return (
            <View>
                <Image
                    source={{ uri: imageUrl }}
                    style={{ width, height, borderRadius: width/2, backgroundColor: colorGreyHover }}
                />
            </View>
        );
    }
}

const ManageChannelDialogStyle = StyleSheet.create({
    innerContainer: {
        gap: 12,
        maxHeight: 500,
    },
});

// This Dialog allows either creating or editing a channel
// FIXME: This function is too long
// eslint-disable-next-line max-lines-per-function
function ManageChannelDialog({ isNewChannel = true, channelKey = "", onClose }: { isNewChannel?: boolean; channelKey?: string; onClose: VoidFunction }) {
    const s = ManageChannelDialogStyle;
    const datastore = useDatastore();
    const canEditChannelInfo = useHasCapability("channel/edit-info");
    const canEditChannelMembers = useHasCapability("channel/edit-members");
    const [channelName, setChannelName] = useState<string>("");
    const [channelPhotoUrl, setChannelPhotoUrl] = useState<string>("");
    const [channelEmail, setChannelEmail] = useState<string>("");
    const [isDefaultChannel, setIsDefaultChannel] = useState(false);
    const [oldMemberEmails, setOldMemberEmails] = useState<string[]>([]);
    const [memberEmails, setMemberEmails] = useState<string[]>([]);
    const channel = useModuleObject(moduleMapChannel, channelKey);

    async function createOrUpdateChannelAsync() {
        await saveChannelInfoAsync();
        await saveChannelMembersAsync();
        onClose();
    }

    async function saveChannelInfoAsync() {
        if (
            canEditChannelInfo &&
            channelPhotoUrl?.length > 0 &&
            isChannelNameValid({ channelName }) &&
            // The channel email address is optional. If it exists, it has to be a valid email address.
            ((channelEmail && isValidEmail({ email: channelEmail })) || !channelEmail)
        ) {
            const newChannelData: Partial<EditorialChannel> = {
                key: channel?.key ?? undefined,
                photoUrl: channelPhotoUrl,
                name: channelName,
                email: channelEmail ?? null,
                isDefault: isDefaultChannel ?? false,
            };

            if (isNewChannel) {
                channelKey = await datastore.callServerAsync("channel", "createChannel", {
                    ...newChannelData,
                });
            } else {
                await datastore.callServerAsync("channel", "updateChannel", {
                    ...newChannelData,
                });
            }
        }
    }

    async function saveChannelMembersAsync() {
        if (!canEditChannelMembers) return;

        const emailsToAdd: string[] = memberEmails.filter((memberEmail) => !oldMemberEmails.includes(memberEmail));
        if (emailsToAdd.length > 0) {
            await datastore.callServerAsync("channel", "addMembersByEmails", {
                emails: emailsToAdd,
                channelKey,
            });
        }

        const emailsToRemove: string[] = oldMemberEmails.filter((oldMemberEmail) => !memberEmails.includes(oldMemberEmail));
        // TODO: Maybe it would be nice to have bulk deletion at some point to avoid triggering a separate server call for every person that gets removed.
        emailsToRemove.forEach(async (emailToRemove) => {
            await datastore.callServerAsync("channel", "removeMemberFromChannel", { email: emailToRemove, channelKey });
        });
    }

    async function getOldMemberEmailsAsync() {
        const channelMemberInfo =
            (await datastore.callServerAsync("channel", "getChannelMembers", { channelKey })) ?? {};
        const emails: string[] = Object.values(channelMemberInfo.memberEmails);
        setOldMemberEmails(emails);
        setMemberEmails(emails);
    }

    useEffect(() => {
        if (!isNewChannel && channel) {
            setChannelName(channel.name);
            setChannelPhotoUrl(channel.photoUrl);
            setIsDefaultChannel(channel.isDefault ?? false);
            getOldMemberEmailsAsync();
            if (channel.email) {
                setChannelEmail(channel.email);
            }
        }
    }, [channel]);

    return (
        <Modal
            onClose={() => onClose()}
            showTopBar={false}
            buttonRow={
                <HorizBox gap={16}>
                    <CTAButton label="Cancel" type="secondary" wide onPress={() => onClose()} />
                    <CTAButton label="Save" onPress={() => createOrUpdateChannelAsync()} wide />
                </HorizBox>
            }
        >
            <PadBox top={32} horiz={20}>
                <View style={s.innerContainer}>
                    <Heading label={isNewChannel ? "Add Channel" : "Edit Channel"} type="large" />
                    {canEditChannelInfo && (
                        <>
                            <ChannelNameFormField channelName={channelName} setChannelName={setChannelName} />
                            <ChannelEmailFormField channelEmail={channelEmail} setChannelEmail={setChannelEmail} />
                            <ChannelImageFormField
                                channelPhotoUrl={channelPhotoUrl}
                                setChannelPhotoUrl={setChannelPhotoUrl}
                            />
                            {!!channelPhotoUrl && <ChannelImagePreview channelPhotoUrl={channelPhotoUrl} />}
                        </>
                    )}
                    <ChannelMemberManagement
                        channelKey={channelKey}
                        isDefaultChannel={isDefaultChannel}
                        memberEmails={memberEmails}
                        setMemberEmails={setMemberEmails}
                    />
                    {canEditChannelInfo && (
                        <ToggleWithLabelWidget
                            labelWidget={DefaultChannelInfoTag}
                            spread
                            value={isDefaultChannel}
                            onChange={setIsDefaultChannel}
                            testID={"default-channel-toggle"}
                            ariaText={"Use as default channel"}
                        />
                    )}
                </View>
            </PadBox>
        </Modal>
    );
}

function ChannelNameFormField({ channelName, setChannelName }) {
    const [error, setError] = useState(false);

    function onChangeName(name) {
        setChannelName(name);
        setError(!isChannelNameValid({ channelName: name }));
    }

    return (
        <FormField label={"Channel Name"} required={true}>
            <TextField
                testID={"channel-name"}
                multiline={false}
                error={error}
                value={channelName}
                onChange={(name) => onChangeName(name)}
            />
        </FormField>
    );
}

function ChannelImageFormField({channelPhotoUrl, setChannelPhotoUrl}) {
    const [error, setError] = useState(false);
    
    function onChangePhotoUrl(url) {
        setChannelPhotoUrl(url);
        setError(url.length === 0);
    }

    return (<FormField label={"Image URL"} required={true}>
        <TextField
            testID={"image-url"}
            multiline={false}
            error={error}
            value={channelPhotoUrl}
            onChange={(url) => onChangePhotoUrl(url)}
        />
    </FormField>);
}

function ChannelEmailFormField({ channelEmail, setChannelEmail }) {
    const [error, setError] = useState(false);

    function onChangeEmail(email) {
        setChannelEmail(email);
        // The channel email address is optional. If it exists, it has to be a valid email address.
        const isEmailFieldValid = isValidEmail({ email }) || email.length === 0;
        setError(!isEmailFieldValid);
    }

    return (
        <FormField label={"Email Address"} required={false}>
            <TextField
                testID={"channel-email"}
                multiline={false}
                error={error}
                value={channelEmail}
                onChange={(email) => onChangeEmail(email)}
            />
        </FormField>
    );
}

function ChannelImagePreview({ channelPhotoUrl }) {
    return (
        <HorizBox gap={12} center>
            <ChannelImage imageUrl={channelPhotoUrl} />
            <UtilityText label="Image preview" color={colorTextSecondaryOnLightColorBg} />
        </HorizBox>
    );
}

function ChannelMemberManagement({ channelKey, isDefaultChannel, memberEmails, setMemberEmails }: { channelKey: string; isDefaultChannel: boolean;  memberEmails: string[]; setMemberEmails: Dispatch<React.SetStateAction<string[]>> }) {
    const [showAddMembersCard, setShowAddMembersCard] = useState(false);
    const canEditChannelMembers = useHasCapability("channel/edit-members");
    if (!canEditChannelMembers) return;

    return (
        <>
            <UtilityText label="Members" weight="medium" type="small" />
            {!showAddMembersCard && (
                <>
                    <AddMembersPressable onPress={() => setShowAddMembersCard(true)} />
                    {memberEmails && <AddedMembersList memberEmails={memberEmails} setMemberEmails={setMemberEmails} />}
                </>
            )}
            {showAddMembersCard && (
                <AddMembersCard
                    memberEmails={memberEmails}
                    setMemberEmails={setMemberEmails}
                    channelKey={channelKey}
                    onClose={() => setShowAddMembersCard(false)}
                />
            )}
            <UtilityText
                label={
                    isDefaultChannel
                        ? "All admin users have access to the default channel."
                        : "All Super-Moderators have access to this channel."
                }
                type="small"
                color={colorTextSecondary}
            />
        </>
    );
}

const AddMembersPressableStyle = StyleSheet.create({
    addMembersPressable: {
        borderRadius: 8,
        borderWidth: 2,
        borderStyle: "dashed",
        backgroundColor: colorSurfaceSecondary,
        height: 48,
        justifyContent: "center",
        paddingHorizontal: 16,
        paddingVertical: 20,
        borderColor: colorBorderPrimary,
    },
});

function AddMembersPressable({ onPress }) {
    const s = AddMembersPressableStyle;
    return (
        <Pressable onPress={onPress} style={s.addMembersPressable} testID="add-members-pressable">
            <HorizBox gap={4}>
                <Add />
                <UtilityText label="Add members" weight="medium" />
            </HorizBox>
        </Pressable>
    );
}

function AddedMembersList({ memberEmails, setMemberEmails }) {
    async function onDeleteUserByEmail(email: string) {
        const updatedMemberEmails = memberEmails.filter((memberEmail) => memberEmail !== email);
        setMemberEmails(updatedMemberEmails);
    }

    return (
        <View>
            <ScrollView style={{ maxHeight: 49 * 5 }}>
                {memberEmails.map((email) => (
                    <UserListEntry
                        key={email}
                        email={email}
                        displayOnly
                        onDelete={onDeleteUserByEmail}
                        checked={false}
                    />
                ))}
            </ScrollView>
        </View>
    );
}

const AddMembersCardStyle = StyleSheet.create({
    cardContainer: {
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: colorBorderPrimary,
        borderRadius: 10,
        padding: 20,
        gap: 24,
        justifyContent: "center",
    },
});

// FIXME: This function is too long
// eslint-disable-next-line max-lines-per-function
function AddMembersCard({ channelKey, onClose, memberEmails, setMemberEmails }: { channelKey: string; onClose: VoidFunction; memberEmails: string[]; setMemberEmails: Dispatch<React.SetStateAction<string[]>> }) {
    const s = AddMembersCardStyle;
    const adminUsersObjects = useServerCallResult("admin", "getAdminUsers", { channelKey });
    const [selectedMembers, setSelectedMembers] = useState(initializeSelectedMembers());

    function initializeSelectedMembers(): Map<string, boolean> {
        const selectedMembers = new Map<string, boolean>();
        memberEmails.forEach(memberEmail => {
            selectedMembers[memberEmail] = true;
        });
        return selectedMembers;
    }

    function toggleMemberSelection(email: string) {
        setSelectedMembers({ ...selectedMembers, [email]: !selectedMembers[email] });
    }

    function onSaveSelectedMembers() {
        let updatedMemberEmails = [...memberEmails];
        for (const [email, isSelected] of Object.entries(selectedMembers)) {
            if (isSelected && !memberEmails.includes(email)) {
                updatedMemberEmails = [...updatedMemberEmails, email];
            } else if (!isSelected && memberEmails.includes(email)) {
                updatedMemberEmails = updatedMemberEmails.filter((memberEmail) => memberEmail !== email);
            }
        }
        setMemberEmails(updatedMemberEmails);
        onClose();
    }

    return (
        <View style={s.cardContainer}>
            <Heading type="small" weight="medium" label="Add members" />
            <TextField
                icon={Search}
                ariaLabel="Member search field"
                placeholder="Search..."
                value={undefS}
                multiline={false}
            />
            <ScrollView style={{ maxHeight: 49 * 5 }}>
                {adminUsersObjects?.map((user) => (
                    <UserListEntry
                        key={user.email}
                        onCheck={toggleMemberSelection}
                        email={user.email}
                        checked={selectedMembers[user.email]}
                    />
                ))}
            </ScrollView>
            <HorizBox center gap={16}>
                <CTAButton label="Add" onPress={onSaveSelectedMembers} />
                <TextButton label="Cancel" onPress={onClose} />
            </HorizBox>
        </View>
    );
}

const UserListEntryStyle = StyleSheet.create({
    innerContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        width: "100%",
    },
    addedMembersPadding: {
        paddingVertical: 8,
        paddingHorizontal: 16,
    },
    emailItemContainer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        flexGrow: 0,
        flexShrink: 1,
        flexBasis: "auto",
        overflow: "hidden",
        gap: 8,
    },
    removeButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 12,
        flexGrow: 0,
    },
});

function UserListEntry({ email, onCheck, displayOnly = false, checked, onDelete }: { email: string; onCheck?: VoidCallBack<string>; displayOnly?: boolean; checked?: boolean; onDelete?: (email) => void }) {
    const s = UserListEntryStyle;
    return (
        <View>
            <View style={[s.innerContainer, displayOnly ? s.addedMembersPadding : null]}>
                <View style={s.emailItemContainer}>
                    <HorizBox gap={8} center>
                        {!displayOnly && <Checkbox onChange={() => onCheck!(email)} value={checked} testID={email} />}
                        <UtilityText text={email} />
                    </HorizBox>
                </View>
                {displayOnly && (
                    <View style={s.removeButton}>
                        <HorizBox right>
                            <ActionButton
                                label="Remove"
                                icon={TrashCan}
                                onPress={() => onDelete!(email)}
                                testID={"remove-" + email}
                            />
                        </HorizBox>
                    </View>
                )}
            </View>
            <Separator />
        </View>
    );
}

export function DefaultChannelInfoTag() {
    return (
        <HorizBox center>
            <UtilityText label={"Use as default channel"} />
            <Pad size={8} />
            <InfoTag
                testID="default-channel-infotag"
                labelWidget={
                    <ShadowBox>
                        <Banner color={colorSurfacePrimary}>
                            <View style={{ width: 300 }}>
                                <UtilityText label="All admin users have access to the default channel." />
                                <Pad size={8} />
                                <UtilityText label="New questions and topics that are not assigned to a specific channel will be assigned to the default channel." />
                                <Pad size={8} />
                                <UtilityText label="There can only be one default channel." />
                            </View>
                        </Banner>
                    </ShadowBox>
                }
            />
        </HorizBox>
    );
}